import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { ConfirmModal } from '../../components/ui';
import { TwoStepModal } from '../../components/TwoStepSecurity';
import appLogo from '../../assets/icon.png';
import type { ReactNode } from 'react';

// Same visual shell as AppLayout (src/components/AppLayout.tsx) — reuses its
// exact CSS classes (app-shell/sidebar/app-main/page-content/page-header) so
// the admin portal looks like it belongs to the same product — but with its
// own admin-specific nav (Dashboard/Businesses/Users/Sessions/Payments,
// client-side tabs rather than routes) instead of AppLayout's tenant-scoped
// NAV_SECTIONS, and without depending on `useAuth().business`/`.user`, which
// are always null for an admin session (admins never resolve a Business doc
// — see AuthContext.tsx's admin branch).

function IconDashboard() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="1" y="1" width="6" height="6" rx="1.5" fill="currentColor" />
      <rect x="9" y="1" width="6" height="6" rx="1.5" fill="currentColor" />
      <rect x="1" y="9" width="6" height="6" rx="1.5" fill="currentColor" />
      <rect x="9" y="9" width="6" height="6" rx="1.5" fill="currentColor" />
    </svg>
  );
}
function IconBusinesses() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M2 14V4.5L8 1.5l6 3V14" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M5.5 14V9.5h5V14" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M5.5 6h1M9.5 6h1M5.5 8.2h1M9.5 8.2h1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}
function IconUsers() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="6" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M1.5 13.5c0-2.5 2-4 4.5-4s4.5 1.5 4.5 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M10.5 3.2a2.3 2.3 0 010 4.4M12.5 13.5c0-2-1.1-3.5-2.7-4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}
function IconSessions() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="2" y="2.5" width="12" height="8.5" rx="1.2" stroke="currentColor" strokeWidth="1.4" />
      <path d="M6 14h4M8 11v3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="8" cy="6.7" r="1.6" fill="currentColor" />
    </svg>
  );
}
function IconPayments() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="1.5" y="4" width="13" height="9" rx="1.3" stroke="currentColor" strokeWidth="1.4" />
      <path d="M1.5 6.8h13" stroke="currentColor" strokeWidth="1.4" />
      <path d="M4 10.3h3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}
function IconLogout() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
      <path d="M6 2H3a1 1 0 00-1 1v9a1 1 0 001 1h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M10 10.5l3-3-3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13 7.5H6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}
function IconSun() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.05 3.05l1.42 1.42M11.54 11.54l1.41 1.41M3.05 12.95l1.42-1.41M11.54 4.46l1.41-1.41"
        stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}
function IconMoon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M13.5 9.5A6 6 0 116.5 2.5a5 5 0 007 7z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  );
}
function IconMenu() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M3 5.5h14M3 10h14M3 14.5h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

export type AdminTab = 'dashboard' | 'businesses' | 'users' | 'sessions' | 'payments';

const NAV_ITEMS: { key: AdminTab; label: string; icon: ReactNode }[] = [
  { key: 'dashboard',  label: 'Dashboard',  icon: <IconDashboard /> },
  { key: 'businesses', label: 'Companies', icon: <IconBusinesses /> },
  { key: 'users',      label: 'Users',      icon: <IconUsers /> },
  { key: 'sessions',   label: 'Sessions',   icon: <IconSessions /> },
  { key: 'payments',   label: 'Payments',   icon: <IconPayments /> },
];

interface AdminLayoutProps {
  children: ReactNode;
  activeTab: AdminTab;
  onTabChange: (tab: AdminTab) => void;
  title?: string;
  subtitle?: string;
  headerActions?: ReactNode;
}

function IconShieldLock() {
  return <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M8 1.7l5 1.8v3.8c0 3.4-2.1 5.9-5 6.7-2.9-.8-5-3.3-5-6.7V3.5l5-1.8Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    <rect x="5.8" y="7.3" width="4.4" height="3.4" rx="0.7" stroke="currentColor" strokeWidth="1.2" />
    <path d="M6.8 7.3V6.3a1.2 1.2 0 0 1 2.4 0v1" stroke="currentColor" strokeWidth="1.2" />
  </svg>;
}

export default function AdminLayout({ children, activeTab, onTabChange, title, subtitle, headerActions }: AdminLayoutProps) {
  const { logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [securityOpen,  setSecurityOpen]  = useState(false);

  // The admin nav is a plain fixed-width sidebar, independent of the
  // tenant-side icon-rail/drawer nav. AppLayout mutates `--sidebar-w` at
  // runtime (60px ↔ 268px as its drawer collapses) and persists it, so an
  // admin session opened after a tenant one would inherit a squished width
  // (and mis-offset modal overlays). Pin it to the admin width while this
  // layout is mounted; AppLayout re-sets it from its own state on remount.
  useEffect(() => {
    document.documentElement.style.setProperty('--sidebar-w', '248px');
    return () => { document.documentElement.style.removeProperty('--sidebar-w'); };
  }, []);

  return (
    <div className="app-shell">
      {/* ── Mobile top bar ── */}
      <div className="mobile-topbar">
        <button className="mobile-menu-btn" onClick={() => setMobileNavOpen(true)} aria-label="Open menu">
          <IconMenu />
        </button>
        <div className="sidebar-logo-icon" style={{ width: 28, height: 28 }}><img src={appLogo} alt="" /></div>
        <div className="mobile-topbar-title">Diamond Admin</div>
      </div>

      {mobileNavOpen && <div className="sidebar-backdrop" onClick={() => setMobileNavOpen(false)} />}

      {/* ── Sidebar — .admin-sidebar carries its own width + mobile
           off-canvas rules so the tenant-side nav rework can't reach it;
           the visuals still come from the shared .sidebar / .sidebar-* CSS. ── */}
      <aside className={`sidebar admin-sidebar${mobileNavOpen ? ' open' : ''}`}>
        <div className="sidebar-logo">
          <div className="sidebar-logo-icon"><img src={appLogo} alt="" /></div>
          <div>
            <div className="sidebar-logo-text">DiaWolt</div>
            <div className="sidebar-logo-sub">Admin</div>
          </div>
        </div>

        <div className="sidebar-nav-scroll">
          <div className="sidebar-section">
            <div className="sidebar-section-label">Control Portal</div>
            {NAV_ITEMS.map(item => (
              <button
                key={item.key}
                type="button"
                className={`sidebar-nav-item${activeTab === item.key ? ' active' : ''}`}
                onClick={() => { onTabChange(item.key); setMobileNavOpen(false); }}
              >
                <span className="sidebar-nav-icon">{item.icon}</span>
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <div className="sidebar-footer">
          <div className="theme-toggle-row">
            <span className="theme-toggle-label">
              <span className="theme-toggle-icon">{theme === 'dark' ? <IconMoon /> : <IconSun />}</span>
              Dark Mode
            </span>
            <button
              type="button"
              className={`theme-switch${theme === 'dark' ? ' on' : ''}`}
              onClick={toggleTheme}
              aria-label="Toggle dark mode"
              aria-pressed={theme === 'dark'}
            />
          </div>
          {/* Order: Dark Mode, Sign in security, then the user with Sign
              Out in the same row at the very bottom. */}
          <button className="sidebar-nav-item" onClick={() => setSecurityOpen(true)}>
            <IconShieldLock />
            Sign in security
          </button>
          <div className="sidebar-user" style={{ display: 'flex', alignItems: 'center' }}>
            <div className="sidebar-avatar">A</div>
            <div className="sidebar-user-info" style={{ flex: 1, minWidth: 0 }}>
              <div className="sidebar-user-name">Super Admin</div>
              <div className="sidebar-user-role">DiaWolt</div>
            </div>
            <button type="button" className="admin-signout-btn" onClick={() => setConfirmLogout(true)}
              title="Sign out" aria-label="Sign out">
              <IconLogout />
            </button>
          </div>
        </div>
      </aside>

      {/* ── Main ── */}
      <main className="app-main">
        <div className="page-content">
          {(title || subtitle || headerActions) && (
            <div className="page-header">
              <div className="page-header-titlerow">
                {title && (
                  <h1 className="page-title">
                    {title}
                  </h1>
                )}
                {headerActions && <div className="page-header-actions">{headerActions}</div>}
              </div>
            </div>
          )}
          {children}
        </div>
      </main>

      <TwoStepModal open={securityOpen} onClose={() => setSecurityOpen(false)} />

      <ConfirmModal
        open={confirmLogout}
        onClose={() => setConfirmLogout(false)}
        onConfirm={logout}
        title="Sign out?"
        message="You'll need to sign in again to get back into the admin portal."
        confirmLabel="Sign Out"
      />
    </div>
  );
}
